import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  RowValue,
} from "../../../../../../components";
import {
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getValueOrBlank,
  getValueOrDash,
  isEmpty,
  isValueIncludedIgnoreCase,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { isProgramFeeType } from "../../../../helper";
import {
  ProgramInvoice,
  ViewInvoiceFeesAndExpenseListItem,
  ViewInvoiceFeesAndExpenses,
} from "../../../../modelsAndSchemas/Models";
import { invoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.PROGRAM_TEMPLATE}-invoice-history-list`;

interface InvoiceFeesAndExpensesState {
  filteredList: ViewInvoiceFeesAndExpenseListItem[];
  originalList: ViewInvoiceFeesAndExpenseListItem[];
}

export const ViewInvoiceFeesAndExpenseList = ({
  data,
  getFormattedValue,
}: {
  data: ViewInvoiceFeesAndExpenseListItem[];
  getFormattedValue: (value: number | undefined) => string;
}) => {
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const { control } = useForm<Partial<ProgramInvoice>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const filters = useWatch({ control });

  const getLabel = (key: keyof typeof invoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      invoiceHistorySiteCoreKeys[key],
    );
  };

  useEffect(() => {
    applyFilters(state.originalList);
  }, [filters]);

  const getProgramFeeLabel = () => {
    return getLabel("programFeeType");
  };

  const programFeeLabel = getProgramFeeLabel();

  const expenseName = (item: ViewInvoiceFeesAndExpenseListItem) =>
    isProgramFeeType(item.typeId) ? programFeeLabel : item.name || "";

  const applyFilters = (
    invoiceData: ViewInvoiceFeesAndExpenseListItem[] = [],
  ) => {
    if (!invoiceData.length) return;

    let filteredList = invoiceData.map((item) => ({
      ...item,
      name: expenseName(item),
    }));

    const {
      name,
      transactionDate,
      typeId,
      expenseCategoryName,
      expenseTypeName,
      billingAmount,
      taxAmount,
      totalBillingAmount,
      lineNumber,
    } = filters || {};

    filteredList = filteredList.filter((item) => {
      const expenseNameValue = expenseName(item);
      return (
        isValueIncludedIgnoreCase(item.lineNumber, lineNumber) &&
        isValueIncludedIgnoreCase(expenseNameValue, name) &&
        isValueIncludedIgnoreCase(
          convertToUserFormat(item.transactionDate, userCountryDateFormat),
          transactionDate,
        ) &&
        isValueIncludedIgnoreCase(item.typeId, typeId) &&
        isValueIncludedIgnoreCase(
          getValueOrBlank(item.expenseCategoryName),
          expenseCategoryName,
        ) &&
        isValueIncludedIgnoreCase(
          getValueOrBlank(item.expenseTypeName),
          expenseTypeName,
        ) &&
        isValueIncludedIgnoreCase(item.billingAmount, billingAmount) &&
        isValueIncludedIgnoreCase(item.taxAmount, taxAmount) &&
        isValueIncludedIgnoreCase(item.totalBillingAmount, totalBillingAmount)
      );
    });

    setState((prevState: InvoiceFeesAndExpensesState) => ({
      ...prevState,
      filteredList,
    }));
    tableRef.current?.resetPagination();
  };

  const [state, setState] = useState<InvoiceFeesAndExpensesState>({
    filteredList: data,
    originalList: data,
  });

  useEffect(() => {
    if (data) {
      const formattedData = data.map((item) => ({
        ...item,
        expenseCategoryName: item?.expenseCategories?.name,
        expenseTypeName: item?.expenseTypes?.name,
        name: expenseName(item),
      }));
      setState({
        filteredList: formattedData,
        originalList: formattedData,
      });
    }
  }, [data, programFeeLabel]);

  const columns: Column[] = [
    {
      id: ViewInvoiceFeesAndExpenses.lineNumber,
      label: getLabel("invoiceLineNumberTableColumn"),
      isSortVisible: true,
    },
    {
      id: ViewInvoiceFeesAndExpenses.name,
      label: getLabel("feeOrExpenseNameTableColumn"),
      isSortVisible: true,
    },
    {
      id: ViewInvoiceFeesAndExpenses.transactionDate,
      label: getLabel("transactionDateTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="Link">
          {row?.transactionDate
            ? convertToUserFormat(row.transactionDate, userCountryDateFormat)
            : EMPTY_VALUE_DASH}
        </MuiTypography>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.typeId,
      label: getLabel("typeTableColumnView"),
      isSortVisible: true,
      minWidth: 200,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {fetchValueByKeyOfDropdown(
              invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
              invoiceHistorySiteCoreKeys.billingItemTypeList,
              row.typeId,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.expenseCategoryName,
      label: getLabel("categoryTableColumnView"),
      isSortVisible: true,
      minWidth: 200,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getValueOrDash(row?.expenseCategoryName)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.expenseTypeName,
      label: getLabel("feeOrExpenseTypeTableColumnView"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getValueOrDash(row?.expenseTypeName)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.billingAmount,
      label: getLabel("billingAmountTableColumnView"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.billingAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.taxAmount,
      label: getLabel("taxAmountTableColumnView"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.taxAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ViewInvoiceFeesAndExpenses.totalBillingAmount,
      label: getLabel("totalBillingAmountTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox className="cell">
          <MuiTypography variant="h3">
            {getFormattedValue(row?.totalBillingAmount)}
          </MuiTypography>
        </MuiBox>
      ),
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.lineNumber}
      key={ViewInvoiceFeesAndExpenses.lineNumber}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.lineNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.name}
      key={ViewInvoiceFeesAndExpenses.name}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.name}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={ViewInvoiceFeesAndExpenses.transactionDate}
      key={ViewInvoiceFeesAndExpenses.transactionDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.typeId}
      key={ViewInvoiceFeesAndExpenses.typeId}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          invoiceHistorySiteCoreKeys.billingItemTypeList,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.typeId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.expenseCategoryName}
      key={ViewInvoiceFeesAndExpenses.expenseCategoryName}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.expenseCategoryName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.expenseTypeName}
      key={ViewInvoiceFeesAndExpenses.expenseTypeName}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.expenseTypeName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.billingAmount}
      key={ViewInvoiceFeesAndExpenses.billingAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.billingAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.taxAmount}
      key={ViewInvoiceFeesAndExpenses.taxAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.taxAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ViewInvoiceFeesAndExpenses.totalBillingAmount}
      key={ViewInvoiceFeesAndExpenses.totalBillingAmount}
    >
      <MuiTextField
        id={`${pageId}-${ViewInvoiceFeesAndExpenses.totalBillingAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <MuiTable
      ref={tableRef}
      pageId={pageId}
      tableHeaderForm={tableHeaderForm}
      columns={columns}
      tableResults={state.filteredList as unknown as Record<string, RowValue>[]}
      noDataInApi={isEmpty(state.originalList)}
      noResultMessage={commonLabels.searchCriteria}
      priorityResults={[]}
      isShowDashIfValueEmpty
    />
  );
};
